/**
 * @module types/jobs
 * @category Type
 */

import { ApgPlusBatchRejectedRow } from '@upright/common/model/input/apg-plus/types';
import { CallbackPayload } from '@upright/common/external-api/openai-batch/types';
import { DirtyWeightSetSizeBucketId } from '@upright/common/model/dirty-weight-set/size-buckets';
import { WithOptionalStartAndEndDate } from '@upright/common/model/data-point-pickers/types';
import { DateRangeAndYear } from '@upright/common/model/date-ranges/types';
import { ApplyMatchingTo, ImportCompaniesRequest } from '@upright/common/model/input/companies/import/api-types';
import { CompanyImportData, CompanyImportRowAndMatch } from '@upright/common/model/input/companies/import/types';
import { InsideviewUpdateConfig } from '@upright/common/model/input/companies/insideview/types';
import { FinnhubSelectiveImportCompanyResult } from '@upright/common/model/input/financial-statements/api-types';
import { SecQuarter } from '@upright/common/model/input/companies/sec/types';
import {
  ScoreCrossValidationResponse,
  ScoreOptimizerProblem,
  ScoreOptimizerResponse,
} from '@upright/common/model/input/constraints/api-types';
import { MatchChangesByOldLegalEntityId } from '@upright/common/model/input/matching/types';
import { TemplateTrace } from '@upright/common/model/input/product-mixins/templates/types';
import { CreateMixinResult } from '@upright/common/model/input/product-mixins/types';
import {
  BusinessSegmentSplitResult,
  ExtractPdfTextResult,
  GhgParseResult,
} from '@upright/common/model/output/companies/types';
import { FullMaterialityResult } from '@upright/common/model/output/materiality/csrd-company-results/types';
import { ModelEntityWithScores } from '@upright/common/model/output/scores/model-entity-data/types';
import { GetWeightSetItemResultsQuery } from '@upright/common/model/output/scores/weight-set-items/api-types';
import { GetFundScreenerResults } from '@upright/common/model/search/fund-screener/api-types';
import { QueryFundScreenerResultsColumnId } from '@upright/common/model/search/fund-screener/types';
import { QueryWeightSetResultConstituentsColumnId } from '@upright/common/model/search/query-entities/query-weight-set-result-constituents/columns';
import { Dictionary } from 'lodash';
import { z } from 'zod';
import {
  CompanyIdentifierTask,
  CompanyIdentifierTaskData,
} from '../../model/input/companies/security-identifier/types';
import {
  ModelEntityType,
  NamedModelEntityReferenceWithGroupType,
  PossiblyNamedModelEntityReference,
} from '../../model/input/types';
import { FlowOptimizerProblem, FlowOptimizerResult, ProductFlowType } from '../../model/output/flows/types';
import { MetricCode } from '../../model/output/metrics/types';
import { ImpactScoreType } from '../../model/output/scores/types';
import {
  BibliometricSource,
  BibliometricUpdateBlock,
  GenericBibliometricUpdateBlock,
  Valence,
} from '../../model/types';
import { SnapshotType } from '../../model/versions/types';
import { getAllEnumValues } from '../../utils';
import {
  CompaniesProblemsJobPayload,
  ConstraintProblemsChildJobPayload,
  ConstraintProblemsJobPayload,
} from '../problems/problem-types';
import { QuandlReleaseData } from '../quandl/types';
import {
  ModelUpdateJobPayload,
  ModelUpdateJobPayloadWithVerboseStarter,
  ModelUpdateStepJobPayload,
  TriggerAutoUpdateJobPayload,
} from './model-update/types';

import { AllMetricsExportQuery } from '@upright/common/external-api/all-metrics/export-types';
import { GroupfolioCsrdExportAggregateData } from '@upright/common/model/output/materiality/csrd-weight-set-results/types-export-data';
import { VariantBulkEdits, VariantDependantsQueueMode } from './variant-edits/types';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';

/**
 * Type of the job (used to route the job to the correct handler)
 */
export enum JobType {
  COMPUTE_COMPANY = 'compute-company',
  COMPUTE_COMPANIES = 'compute-companies',
  COMPUTE_WEIGHT_SET = 'compute-weightset',
  COMPUTE_WEIGHT_SETS = 'compute-weightsets',
  MATCH_WEIGHT_SET_ITEMS = 'match-weight-set-items',
  MATCH_IMPORT_SHEET = 'match-import-sheet',
  REMATCH_WEIGHT_SETS = 'rematch-weight-sets',
  BULK_IMPORT_CONSTITUENTS = 'bulk-import-constituents',
  QUANDL_EXPORT = 'quandle-export',
  PRODUCT_TERM_MATCH_COUNT = 'product-term-match-count',
  TERM_PAIR_MATCH_COUNT = 'term-pair-match-count',
  ARTICLE_MATCH_PREDICTION = 'article-match-prediction',
  TERM_PAIR_PREDICTION = 'term-pair-prediction',
  MERGED_ARTICLE_MATCH_PREDICTION = 'merged-article-match-prediction',
  FULL_SCORES_LEAF = 'full-scores-leaf',
  FULL_SCORES_LEAF_V2 = 'full-scores-leaf-v2',
  FINAL_SCORES_NONLEAF = 'final-scores-non-leaf',
  FINAL_SCORES_NONLEAF_V2 = 'final-scores-non-leaf-v2',
  MODEL_UPDATE_STEP = 'update-step',
  SEC_FUND_IMPORT = 'sec-fund-import',
  SEC_EDGAR_UPDATE = 'sec-edgar-update',
  INSIDEVIEW_IMPORT = 'insideview-import',
  OPTIMIZED_FLOWS = 'optimized-flows',
  FLOW_OPTIMIZATION = 'flow-optimization',
  FLOW_POSTPROCESSING = 'flow-postprocessing',
  CONSTRAINED_SCORES = 'constrained-scores',
  SCORE_OPTIMIZATION = 'score-optimization',
  SCORE_CROSS_VALIDATION = 'score-cross-validation',
  MODEL_UPDATE = 'model-update',
  TRIGGER_AUTO_MODEL_UPDATE = 'trigger-auto-update',
  SNAPSHOT = 'snapshot',
  AUTO_DOWNGRADE_SNAPSHOTS = 'auto-downgrade-snapshots',
  AUTO_DELETE_SNAPSHOTS = 'auto-delete-snapshots',
  COMPANIES_PROBLEMS = 'companies-problems',
  POPULATE_COMPANY_IDENTIFIERS = 'populate-company-identifiers',
  ALLOCATION_MATRIX = 'allocation-matrix',
  ALLOCATION_MATRIX_V2 = 'allocation-matrix-v2',
  ALLOCATION_TABLE_UPDATE = 'allocation-table-update',
  ALLOCATION_TABLE_UPDATE_V2 = 'allocation-table-update-v2',
  METRIC = 'metric',
  SEGMENT_SPLIT_EXTRACTION = 'segment-split-extraction',

  CALLM_CONTROL = 'callm-control',
  LLM_BATCH_JOB = 'llm-batch-job',
  // GISE related
  IMPACT_STATEMENT_FACT_EVALUATION = 'impact-statement-fact-evaluation',
  GISE_IMPACT_TOPIC_EXTRACTION_BATCH = 'impact-topic-extraction-batch',

  ON_DEMAND_MATERIALITY_ASSESSMENT = 'on-demand-materiality-assessment',
  SEGMENT_SPLIT_VALIDATION = 'segment-split-validation',

  CSRD_PRODUCT_TRIGGERS = 'csrd-product-triggers',

  CSRD_BULK_PREPARE_INPUT_SETS = 'csrd-bulk-prepare-input-sets',

  BULK_UPDATE_VARIANTS = 'bulk-update-variants',
  RECOMPUTE_PRE_RELEASE_FOR_COMPANIES = 'recompute-pre-release-for-companies',
  CONSTRAINT_PROBLEMS = 'constraint-problems',
  CONSTRAINT_PROBLEMS_CHILD_JOB = 'constraint-problems-child-job',
  TRIGGER_AUTO_CONSTRAINT_PROBLEMS_JOB = 'trigger-auto-constraint-problems-job',
  TRIGGER_AUTO_TAXONOMY_PROBLEMS_JOB = 'trigger-auto-taxonomy-problems-job',
  UPDATE_SCREENER_ROWS = 'update-screener-rows',
  PRUNE_SCREENER_TABLE = 'prune-screener-table',
  REMAKE_SCREENER_TABLE = 'remake-screener-table',
  MIXIN_SCORE_UPDATE = 'mixin-score-update',
  CREATE_MIXIN = 'create-mixin',
  COMPUTE_MIXIN_EXPRESSION = 'compute-mixin-expression',
  RECOMPUTE_MIXIN = 'recompute-mixin',
  SEARCH_REFRESH_ELASTIC_SEARCH_ENTITY_INDEX = 'search-refresh-elastics-search-entity-index',
  SEARCH_REFRESH_SEMANTIC_SEARCH_PRODUCT_INDEX = 'search-refresh-semantic-search-product-index',
  EXTRACT_PDF_TEXT = 'extract-pdf-text',
  COMPUTE_COMPANY_VARIANT_RESULT = 'compute-company-variant-result',
  COMPUTE_COMPANY_VARIANT_RESULTS = 'compute-company-variant-results',
  COMPUTE_COMPANY_VARIANT_MATERIALITY = 'compute-company-variant-materiality',
  NOTIFY_AFTER_API_IMPORTS = 'notify-after-api-imports',
  AUTOGPT_WEBSITE_CRAWL = 'autogpt-website-crawl', // OH: Deprecated and replaced with GPT_PRODUCT_SET_GENERATION
  AUTOGPT_WEBSITE_CRAWL_AND_REPORT_PARSE = 'autogpt-website-crawl-and-report-parse',
  GPT_PRODUCT_SET_GENERATION = 'generate-gpt-product-set',
  REVENUE_SEGMENTS_PRODUCT_SET_GENERATION = 'generate-revenue-segments-product-set',
  REVENUE_SEGMENTS_PRODUCT_SET_BATCH = 'generate-revenue-segments-product-set-batch',
  ASYNC_FUND_BUILD = 'async-fund-build',
  // PLT-7298: once a fund created or extended in chat has computed, post its coverage into the chat.
  CHAT_FUND_COVERAGE_REPORT = 'chat-fund-coverage-report',
  EXPORT_WEIGHT_SET_ITEMS = 'export-weight-set-items',
  EXPORT_FUND_SCREENER_RESULTS = 'export-fund-screener-results',
  EXPORT_GROUPFOLIO_CSRD_DATA = 'export-groupfolio-csrd-data',
  EXPORT_GROUPFOLIO_CSRD_CONSTITUENT_DATA = 'export-groupfolio-csrd-constituent-data',
  EXPORT_EXTERNAL_API_ALL_METRICS = 'export-external-api-all-metrics',

  PUBLISH_IPR_PRODUCT_SET = 'publish-ipr-product-set',
  // PLT-6996: a custom company's physical sites, identified at creation next to its
  // product-set generation and saved as a draft site-location set for review.
  IDENTIFY_COMPANY_SITES = 'identify-company-sites',
  // PLT-7182: write the stored executive summary, buyer actions and topic lines of an
  // organization's suppliers, as each buyer. Queued after a supplier's results are saved,
  // and by the team-only refresh route.
  WRITE_SUPPLIER_RISK_WRITE_UPS = 'write-supplier-risk-write-ups',
  PREPARE_SUPPLIER_RISK = 'prepare-supplier-risk',
  // PLT-7095: the websites of a chat supplier import's rows that had none, looked up a few at a time.
  SUPPLIER_IMPORT_WEBSITE_LOOKUP = 'supplier-import-website-lookup',
  // PLT-7097: the suppliers the user created from a chat supplier import's review card, a few at a time.
  SUPPLIER_IMPORT_CREATE = 'supplier-import-create',

  FINNHUB_REFRESH_FUND_DATA = 'finnhub-refresh-fund-data',
  FINNHUB_FETCH_TO_STAGING = 'finnhub-fetch-to-staging',
  FINNHUB_UPSERT_FROM_STAGING = 'finnhub-upsert-from-staging',
  FINNHUB_REFRESH_COMPANY_FINANCIAL_STATEMENTS = 'finnhub-refresh-company-financial-statements',
  FINNHUB_REFRESH_COMPANIES_FINANCIAL_STATEMENTS = 'finnhub-refresh-companies-financial-statements',
  /**
   * PLT-6018: import only the requested company/year pairs and link the result to named
   * variants. One job per company, queued below IMMEDIATE so a batch does not contend with
   * UI-triggered work.
   */
  FINNHUB_SELECTIVE_IMPORT_FINANCIAL_STATEMENTS = 'finnhub-selective-import-financial-statements',

  STABILIZE_RELEASE = 'stabilize-release',
  ARCHIVE_DATA = 'archive-data',
  BATCH_REQUEUE_DIRTY_WEIGHT_SETS = 'batch-requeue-dirty-weight-sets',

  LEENA2_HARNESS = 'leena2-harness',
  /**
   * A single leena-2 DEBUG run executed on upwork (PLT-6668). Its own type, and
   * its own queue, so that a batch of hundreds of debug runs cannot occupy the
   * LEENA2_HARNESS slots the company runner's scoring runs need.
   */
  LEENA2_DEBUG_RUN = 'leena2-debug-run',
  /**
   * Regenerate a product's CPD intra comparisons for chosen impact topics with
   * the uptrain teacher (PLT-6939). Its own queue: the calls go to DashScope,
   * not the Leena-2 endpoint, and take minutes each.
   */
  LEENA2_TEACHER_COMPARISONS = 'leena2-teacher-comparisons',
  /**
   * Boost chosen impact topics' intra units with extra Leena-2 (student)
   * comparisons (PLT-6950). Its own queue, so a long boost never holds the
   * scoring slots; endpoint load is bounded by the in-flight semaphore.
   */
  LEENA2_STUDENT_SAMPLING = 'leena2-student-sampling',
  /**
   * Ask a Leena-2 backend one `cpd_single` question per (product, impact
   * topic) of a list and store the answers (PLT-7946). Writes no product
   * results; its own queue, like student sampling.
   */
  LEENA2_SINGLE_ANSWERS = 'leena2-single-answers',
  /**
   * A Leena-2 global / subset solve (PLT-6923): one joint sparse solve over
   * cached comparison units. CPU- and memory-bound on the worker rather than
   * endpoint-bound, so it has its own single-slot queue.
   */
  LEENA2_GLOBAL_SOLVE = 'leena2-global-solve',

  // PLT-5413: daily ingest of LLM/AI provider cost into llm_cost_daily for the
  // unified cost dashboard. Self-rescheduling recurring job, plus a one-shot
  // manual refresh (distinguished by payload.oneShot).
  INGEST_LLM_COSTS = 'ingest-llm-costs',
  // Nightly derivation of the entitlement record from the legacy access tables. Self-rescheduling
  // like INGEST_LLM_COSTS; nothing enqueues it, so it runs only once someone starts it.
  DERIVE_LEGACY_ENTITLEMENTS = 'derive-legacy-entitlements',
  // One run of the pilot activation preview for the sponsors the payload names. Runs once and
  // completes; nothing enqueues it.
  PREVIEW_SPONSOR_ACTIVATION = 'preview-sponsor-activation',
  // The daily expiry sweep of pilot projections, one retire command per sponsor. Self-rescheduling;
  // seeded at upwork-master startup.
  RETIRE_EXPIRED_ENTITLEMENT_CONTRIBUTIONS = 'retire-expired-entitlement-contributions',
  // The daily read-only adoption report (activation preview and drift) of every pilot sponsor.
  // Self-rescheduling; seeded at upwork-master startup.
  REPORT_ENTITLEMENT_ADOPTION = 'report-entitlement-adoption',
  // Daily: deletes the content of Arvo decisions past their retention, and expired drafts.
  // Self-rescheduling like INGEST_LLM_COSTS (upserve/modules/arvo/retention-job.ts).
  ARVO_RETENTION_SWEEP = 'arvo-retention-sweep',
  /**
   * PLT-7394: build the hazard map tiles of one hazard × scenario × stored year that
   * this environment's bucket does not hold yet. Queued per year by
   * `POST /api/materiality/climate/hazard-map/prefill` after a climate data release;
   * re-running skips what is done.
   */
  HAZARD_MAP_TILE_PREFILL = 'hazard-map-tile-prefill',
  /**
   * @deprecated
   */
  FULL_SCORE_CONTRIBUTION = 'full-score-contribution',
}

export const allJobTypes = getAllEnumValues(JobType, 'string');

/**
 * These jobs we like to keep for logging and general tracking purposes.
 *
 * IDENTIFY_COMPANY_SITES is kept because its row is state, not a log: it is the record that a
 * company's sites were already searched for, which stops the publish job and a repeat creation
 * from paying for a second run (PLT-6996). One row per custom company.
 */
export const jobTypesToKeep = [
  JobType.MODEL_UPDATE,
  JobType.MODEL_UPDATE_STEP,
  JobType.ARCHIVE_DATA,
  JobType.IDENTIFY_COMPANY_SITES,
];

export const backgroundJobTypes = [
  JobType.AUTO_DOWNGRADE_SNAPSHOTS,
  JobType.AUTO_DELETE_SNAPSHOTS,
  JobType.TRIGGER_AUTO_MODEL_UPDATE,
  JobType.TRIGGER_AUTO_CONSTRAINT_PROBLEMS_JOB,
  JobType.TRIGGER_AUTO_TAXONOMY_PROBLEMS_JOB,
  // PLT-4133. Kept in this list so `upwork-slave.ts:101` routes failures to
  // logger.error rather than logger.warn — a batch orchestrator
  // crashing is an error-level event.
  JobType.BATCH_REQUEUE_DIRTY_WEIGHT_SETS,
  // PLT-5413: self-rescheduling daily cost ingest — a crash is error-level.
  JobType.INGEST_LLM_COSTS,
  JobType.DERIVE_LEGACY_ENTITLEMENTS,
  JobType.ARVO_RETENTION_SWEEP,
  JobType.RETIRE_EXPIRED_ENTITLEMENT_CONTRIBUTIONS,
  JobType.REPORT_ENTITLEMENT_ADOPTION,
];

export interface ComputeVariantResultJobPayload {
  type: JobType.COMPUTE_COMPANY_VARIANT_RESULT;
  variantId: string;
  dependantsQueueMode: VariantDependantsQueueMode;
  forceEvenIfStable: boolean;
}

export interface ComputeVariantResultBatchJobPayload {
  type: JobType.COMPUTE_COMPANY_VARIANT_RESULTS;
  variantIds: string[];
  dependantsQueueMode: VariantDependantsQueueMode;
}

export interface ComputeCompaniesJobPayload {
  type: JobType.COMPUTE_COMPANIES;
  version: string;
  ids: string[];
}

export interface ComputeCompanyJobPayload {
  type: JobType.COMPUTE_COMPANY;
  id: string; // This is the COMPANY id
}

export interface NotifyAfterApiImportsPayload {
  type: JobType.NOTIFY_AFTER_API_IMPORTS;
  userId: number;
  entityRefs: PossiblyNamedModelEntityReference[];
}

export interface ComputeCompaniesJobPayload {
  type: JobType.COMPUTE_COMPANIES;
  ids: string[];
}

export interface ComputeWeightSetJobPayload {
  type: JobType.COMPUTE_WEIGHT_SET;
  // As of 10/2024:
  // - For company groups
  //   - only computes the proided weight set, the entityId and entityType are for convenience
  //   - only computes for the provided release (which must be one of liveUpdateModelReleaseNumbers)
  //   - filters the computation for the provided userGroupIds
  //     - if the userGroupIds provided are something that would not be chosen otherwise in computation,
  //       they will be ignored
  //
  // - For portfolios,
  //   - always computes all weight sets of the portfolio, regardless of the provided weightSetId
  //   - similarly, userGroupIds will not filter anything
  //   - similarly, always computes for liveUpdateModelReleaseNumbers instead of the provided release
  //
  // TODO: Update the portfolio computation side to respect the parameters
  weightSetId: number;
  userGroupIds?: number[];
  release: string;

  entityType: ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;
  entityId: string;
}

export interface ComputeWeightSetsJobPayload {
  type: JobType.COMPUTE_WEIGHT_SETS;
  release: string;
  entityType: ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;
  entityIds: string[];
  /**
   * How many parent re-enqueue hops deep this job is in the compute-weightsets cascade.
   * The initial fan-out is depth 0; each successor enqueued for a ready parent group
   * increments it. Acts as a hard backstop against runaway loops when the group
   * containment graph contains cycles (e.g. two funds that list each other). See
   * MAX_CASCADE_DEPTH in company-group-results/index.ts. Omitted == 0.
   */
  cascadeDepth?: number;
}

export interface MatchWeightSetItemsJobPayload {
  type: JobType.MATCH_WEIGHT_SET_ITEMS;
  weightSetId: number;
  matchedItems?: CompanyImportRowAndMatch[];
}

export interface MatchImportSheetJobPayload extends ImportCompaniesRequest {
  type: JobType.MATCH_IMPORT_SHEET;
}

export interface RematchWeightSetsJobPayload {
  type: JobType.REMATCH_WEIGHT_SETS;
  applyMatchingTo: ApplyMatchingTo;
  primaryUserGroupId: number | undefined;
  userId: number;
  changes: MatchChangesByOldLegalEntityId;
}

export interface BulkImportConstituentsJobPayload {
  type: JobType.BULK_IMPORT_CONSTITUENTS;
  startAndEndDate: WithOptionalStartAndEndDate;
  entityRef: NamedModelEntityReferenceWithGroupType;
  userId: number;
  constituents: any; // TODO typing PortfolioConstituents
  useNmpForModelling: boolean;
}

export interface QuandlExportJobPayload {
  type: JobType.QUANDL_EXPORT;
  data?: QuandlReleaseData;
}

export interface ProductTermMatchCountJobPayload {
  type: JobType.PRODUCT_TERM_MATCH_COUNT;
  block: BibliometricUpdateBlock;
  since: Date | undefined;
  batchId: number;
}

export interface ArticleMatchPredictionJobPayload {
  type: JobType.ARTICLE_MATCH_PREDICTION;
  block: GenericBibliometricUpdateBlock;
  since: Date | undefined;
  batchId: number;
}

export interface FullScoresJobPayload {
  type:
    | JobType.FULL_SCORES_LEAF
    | JobType.FINAL_SCORES_NONLEAF
    | JobType.FULL_SCORES_LEAF_V2
    | JobType.FINAL_SCORES_NONLEAF_V2;
  productCode: string;
  batchId: number;
}

export interface TermPairMatchCountJobPayload {
  type: JobType.TERM_PAIR_MATCH_COUNT;
  block: GenericBibliometricUpdateBlock;
  since: Date | undefined;
  batchId: number;
}

export interface MergedArticleMatchPredictionJobPayload {
  type: JobType.MERGED_ARTICLE_MATCH_PREDICTION;
  block: GenericBibliometricUpdateBlock;
  since: Date | undefined;
  batchId: number;
}

export interface OptimizedFlowsJobPayload {
  type: JobType.OPTIMIZED_FLOWS;
  flowType: ProductFlowType;
  flowProblem: FlowOptimizerProblem;
  result: FlowOptimizerResult | undefined;
}

export interface OptimizedScoresJobPayload {
  type: JobType.CONSTRAINED_SCORES;
  impactCode: string;
  valence: Valence;
  sourceScores: ImpactScoreType;
  targetScores: ImpactScoreType;
  batchId: number;
}

export interface ScoreOptimizationJobPayload {
  type: JobType.SCORE_OPTIMIZATION;
  problem: ScoreOptimizerProblem;
}

export interface ScoreCrossValidationJobPayload {
  type: JobType.SCORE_CROSS_VALIDATION;
  problem: ScoreOptimizerProblem;
  folds: number;
}

export interface FlowOptimizationJobPayload {
  type: JobType.FLOW_OPTIMIZATION;
  fileId: string;
}

export interface InlineFlowOptimizationJobPayload {
  type: JobType.FLOW_OPTIMIZATION;
  data: FlowOptimizerProblem;
}

export interface FlowPostprocessingJobPayload {
  type: JobType.FLOW_POSTPROCESSING;
  optimizationJobId: number;
  flowType: ProductFlowType;
}

export interface SecFundImportJobPayload {
  type: JobType.SEC_FUND_IMPORT;
  username: string;
  groupId: string;
  /**
   * @deprecated
   */
  manualRequest?: boolean;
}

export interface SecEdgarUpdateJobPayload {
  type: JobType.SEC_EDGAR_UPDATE;
  username: string;
  year: number;
  quarter: SecQuarter;
}

export interface InsideivewImportJobPayload {
  type: JobType.INSIDEVIEW_IMPORT;
  username: string;
  groupId?: string; // company group to update or overwrite
  companyIds?: string[];
  externalIds?: string[]; // company group to import (and overwrite group with!)
  updateConfig?: InsideviewUpdateConfig;
}

export interface SnapshotJobPayload {
  type: JobType.SNAPSHOT;
  snapshotType: SnapshotType;
  version: string;
  description: string;
  usingVersion?: string; // Optional spec to take the snapshot from something else than master
}

export interface MetricJobPayload {
  type: JobType.METRIC;
  code: MetricCode;
  version: string;
  refVersion?: string;
}

export interface SnapshotDowngradeJobPayload {
  type: JobType.AUTO_DOWNGRADE_SNAPSHOTS;
}

export interface SnapshotDeleteJobPayload {
  type: JobType.AUTO_DELETE_SNAPSHOTS;
}

export interface PopulateCompanyIdentifiersJobPayload {
  type: JobType.POPULATE_COMPANY_IDENTIFIERS;
  task: CompanyIdentifierTask;
  taskData?: CompanyIdentifierTaskData;
}

export interface TriggerAutoConstraintProblemsJobJobPayload {
  type: JobType.TRIGGER_AUTO_CONSTRAINT_PROBLEMS_JOB;
}

export interface TriggerAutoTaxonomyProblemsJobJobPayload {
  type: JobType.TRIGGER_AUTO_TAXONOMY_PROBLEMS_JOB;
}

export interface AllocationMatrixJobPayload {
  type: JobType.ALLOCATION_MATRIX;
  batchId: number;
  productCode: string;
}

export interface AllocationMatrixJobV2Payload {
  type: JobType.ALLOCATION_MATRIX_V2;
  batchId: number;
  productCodes: string[];
}

export interface AllocationTableUpdateJobPayload {
  type: JobType.ALLOCATION_TABLE_UPDATE | JobType.ALLOCATION_TABLE_UPDATE_V2;
  parentId: number;
}

/**
 * @deprecated
 */
export interface FullScoreContributionPayload {
  type: JobType.FULL_SCORE_CONTRIBUTION;
  batchId: number;
  productCode: string;
  dependencies: string[];
}

export interface TermPairPredictionPayload {
  type: JobType.TERM_PAIR_PREDICTION;
  source: BibliometricSource;
  productCode: string;
}

export enum ScreenerTableType {
  FUND = 'fund',
  COMPANY = 'company',
}

export type UpdateScreenerRowsJobPayload =
  | {
      type: JobType.UPDATE_SCREENER_ROWS;
      release: string;
      screenerTableType: ScreenerTableType.COMPANY;
      entityIds: string[];
      scenarioIdPrioList: string[] | undefined; // Instead of defaults, use these scenarios to select variant
    }
  | {
      type: JobType.UPDATE_SCREENER_ROWS;
      version: string;
      screenerTableType: ScreenerTableType.FUND;
      entityIds: string[];
    };

export interface PruneScreenerTableJobPayload {
  type: JobType.PRUNE_SCREENER_TABLE;
  screenerTableType: ScreenerTableType.FUND;
  version?: string;
}

export interface RemakeScreenerTableJobPayload {
  type: JobType.REMAKE_SCREENER_TABLE;
  releases: string[] | 'all';
  scenarioIdPrioList?: string[];
}

export interface SearchRefreshElasticSearchEntityIndexJobPayload {
  type: JobType.SEARCH_REFRESH_ELASTIC_SEARCH_ENTITY_INDEX;
}

export interface SearchRefreshSemanticSearchProductIndexJobPayload {
  type: JobType.SEARCH_REFRESH_SEMANTIC_SEARCH_PRODUCT_INDEX;
}

export interface BaseAutoGPTJobPayload {
  companyId: string;
  maxWebsitesToVisit: number;
  documentUrl?: string;
  browseDepth: number;
  batchId: string;
  useLLMToVerifyProductMatches: boolean;
  segments?: { [k: string]: number | null }; // Note: since we are reading from JSONB, this is read as an OBJECT from DB, one has to manually cast to a Map
  segmentYear?: number;
  segmentBatchId?: string;
  /**
   * Publishes the product set and attaches it to the company variant that is the latest time version
   * in the latest release and belongs to the variant scenario of the user group + organization linked to this company.
   * If no such variant exists, creates one.
   */
  shouldUpsertVariant?: boolean;
}

// No document URL, therefore fast and can go through the GPT-3.5 pipeline
export interface AutoGPTWebsiteCrawlJobPayload extends BaseAutoGPTJobPayload {
  type: JobType.AUTOGPT_WEBSITE_CRAWL;
  url: string;
  userId: number;
}

// Has document URL, therefore it needs to go through the GPT-4 pipeline which is limited and costly
export interface AutoGPTWebsiteCrawlJobAndReportParsePayload extends BaseAutoGPTJobPayload {
  type: JobType.AUTOGPT_WEBSITE_CRAWL_AND_REPORT_PARSE;
  url: string;
  userId: number;
}

/**
 * What kicked off a product-set generation. The value is durable — it is stamped onto the
 * job payload and onto the blanket set's `sourceMetadata` — so it is both a routing signal
 * and the only after-the-fact record of which flow created a company.
 *
 * Never compare a trigger directly. Ask `gpt-product-set-trigger-capabilities.ts` what the
 * trigger *does*; that module explains why, and its matrix is exhaustive, so adding a member
 * here is a compile error until every capability has been answered for it.
 */
export enum GPTProductSetGenerationTrigger {
  CORPORATE_DMA_WAITLIST = 'corporate-dma-waitlist',
  CORPORATE_DMA_SIGNUP = 'corporate-dma-signup',
  CONSULTANT_DMA = 'consultant-dma',
  INVESTOR_DD = 'investor-dd',
  // PLT-6290: a DMA Freemium or DMA Consultant organization screening a supplier. Runs the
  // same pipeline as INVESTOR_DD; it exists so the Suppliers list, the supplier quota and
  // the chat-vs-form metric can tell the two apart.
  SUPPLIER_DD = 'supplier-dd',
  GENERIC = 'generic',
}

/** Where a company was created: the platform's forms, the chat (web chat or connector), or a supplier import. */
export const allCompanyCreationSurfaces = ['chat', 'form', 'import'] as const;
export type CompanyCreationSurface = (typeof allCompanyCreationSurfaces)[number];

export interface GPTProductSetGenerationJobPayload {
  type: JobType.GPT_PRODUCT_SET_GENERATION;
  companyId: string;
  revenueInput?: {
    revenue: number;
    revenueCurrency: string;
    revenueYear: number;
  };
  employeeCountInput?: {
    employeeCount: number;
    employeeCountYear: number;
  };
  trigger: GPTProductSetGenerationTrigger;
  url: string;
  userId: number;
  useMixin: boolean;
  /**
   * PLT-5610: mark the generated Blanket product set as an IPRA blanket so it
   * opens with the attribute-editing tools. The "Create new GPT product set"
   * modal ties this to a single "IPRA blanket" checkbox that also flips
   * `useMixin` off (the attribute/IPR-alias-mixin route). Optional/additive:
   * omitted by other callers (e.g. APG mini) and treated as a regular blanket.
   */
  isIpra?: boolean;
  /**
   * Publishes the product set and attaches it to the company variant that is the latest time version
   * in the latest release and belongs to the variant scenario of the user group + organization linked to this company.
   * If no such variant exists, creates one.
   */
  shouldUpsertVariants?: boolean;
  forceRegenerate?: boolean;
  apg_mini?: boolean;
  headquarterCountry?: string;
  sourceOverride?: string;
  // PLT-5491: optional user-provided guidance about the company, stored with the
  // run. It stays on the platform: never sent to the mapping service (PLT-7336).
  additionalContext?: string;
  // PLT-5505: when this generation was kicked off from a `/chat` conversation
  // (the `create_dd_company` tool), this carries the originating conversation so
  // the completion hook can persist a "product set ready" assistant message back
  // into that thread (it surfaces on reopen/refresh). Optional/additive: external
  // MCP-server callers leave it unset and the hook no-ops. `payload` is JSONB, so
  // no migration is needed.
  chatConversationId?: string;
  /**
   * PLT-7086: the run is stale once the company's website is no longer `url`. A custom company
   * whose website the user corrects after creation gets a new run, and a stale one must not write
   * its product set over the new run's (a run cannot be cancelled, so the newest write would win).
   * Set by `triggerGptProductSetGenerationForCompany` for the custom-company triggers
   * (`isCustomCompanyAssessment`), the companies whose website that correction can change. Not by
   * the team endpoint, which may generate from any URL.
   */
  staleWhenCompanyWebsiteChanges?: boolean;
  /**
   * PLT-6285: identifies a published company's products again, from the website the user changed it
   * to. The new set replaces the company's drafts, keeps the revenue and employee figures, and waits,
   * like any edit, for a recalculation. The company overview and HQ country are left as they are.
   */
  reidentify?: boolean;
  /**
   * PLT-7638: where the company was created, for the team's Slack messages about it. Unset on runs
   * queued from the team's own tools, and on runs queued before it was recorded.
   */
  createdVia?: CompanyCreationSurface;
}

/**
 * PLT-6996: identify a custom company's physical sites while its product set is generated, and
 * save them as the company's draft site-location set, so the user can review them before the
 * first publish. The job row is also the record that identification was started for the
 * company: the publish job reads it to decide whether to wait, or to identify sites itself.
 */
export interface IdentifyCompanySitesJobPayload {
  type: JobType.IDENTIFY_COMPANY_SITES;
  companyId: string;
  /** The creating user: the actor on the draft and the run log. */
  userId: number;
  trigger: GPTProductSetGenerationTrigger;
  /**
   * PLT-6285: identifies a published company's sites again, from the website the user changed it to.
   * The sites replace the company's site draft and wait, like any edit, for a recalculation.
   */
  reidentify?: true;
}

/**
 * PLT-7182: write the stored write-ups of one organization's suppliers, each as its buyer.
 *
 * Every filter narrows; with none, every supplier of every buyer in the organization. A supplier
 * whose stored text still matches its content is skipped unless `force` is set, so a refresh over
 * the whole organization costs a model call only per supplier that has changed.
 */
export interface WriteSupplierRiskWriteUpsJobPayload {
  type: JobType.WRITE_SUPPLIER_RISK_WRITE_UPS;
  organizationId: number;
  /** Only this buyer's suppliers. */
  buyerCompanyId?: string;
  /** Only these suppliers: the companies whose results were just saved. */
  supplierCompanyIds?: string[];
  /** Write afresh even where the stored text is current. */
  force?: boolean;
  /** One durable prepared generation. Omitted by legacy/manual refresh jobs. */
  preparation?: {
    supplierCompanyId: string;
    generation: number;
    contextRevision: string;
    release: string;
    scoringVersion: string;
    narrativeVersion: string;
  };
}

/** One durable numerical preparation generation; its row owns retries and publication. */
export interface PrepareSupplierRiskJobPayload {
  type: JobType.PREPARE_SUPPLIER_RISK;
  organizationId: number;
  buyerCompanyId: string;
  supplierCompanyId: string;
  generation: number;
}

/** What a WRITE_SUPPLIER_RISK_WRITE_UPS job did, by supplier. Stored as the job's result. */
export interface WriteSupplierRiskWriteUpsJobResult {
  suppliers: number;
  /** Text written by the model, or taken from the chat card's cache for the same content. */
  written: number;
  /** Stored text still current; nothing written. */
  skipped: number;
  /** Nothing to write: no row worth writing about, or no score the buyer may be shown. */
  nothingToWrite: number;
  /** The model call failed or threw; the supplier is written on its next open, or the next refresh. */
  failed: number;
}

/**
 * PLT-7095: look up the website of every row of a supplier import that has none. Resumable: each
 * perform looks up a few rows and yields, and the rows themselves record which are done, so the job
 * carries no state of its own beyond the fire-once marker for its closing chat message.
 */
export interface SupplierImportWebsiteLookupJobPayload {
  type: JobType.SUPPLIER_IMPORT_WEBSITE_LOOKUP;
  /** The `chat_supplier_import_staging` row. */
  importId: string;
  /** The uploading user, whom the lookups run for and who alone may read the import. */
  userId: number;
}

export interface SupplierImportWebsiteLookupJobResult {
  supplierWebsiteLookup: {
    /** Set once the job paused before retrying rows whose first lookup could not search. */
    retryPauseTakenAt?: string;
    /** Set once the organization ran out of lookups for the day (PLT-7507), for the closing message. */
    dailyLimitReachedAt?: string;
    /** Set once every row has its answer. */
    finishedAt?: string;
    /** Fire-once marker for the message posted into the conversation. */
    chatMessageInjectedAt?: string;
  };
}

/**
 * PLT-7097: create the rows of a supplier import the user created from its review card. Resumable as
 * the website lookup is: each perform creates a few rows and yields, and each row records how its
 * creation went, so a requeued job creates no supplier twice.
 */
export interface SupplierImportCreateJobPayload {
  type: JobType.SUPPLIER_IMPORT_CREATE;
  /** The `chat_supplier_import_staging` row. */
  importId: string;
  /** The uploading user, whom the suppliers are created as. */
  userId: number;
}

export interface SupplierImportCreateJobResult {
  supplierImportCreate: {
    /**
     * PLT-7100: set when no row was left queued and the job began following the created suppliers'
     * assessments; cleared when the user creates more meanwhile. The job gives up following a fixed
     * time after it.
     */
    followingSince?: string;
    /**
     * PLT-7284: set when the job first finds the in-flight limits reached with rows still queued, and
     * cleared when it creates again, so that the pause and the resume are each logged once.
     */
    pausedSince?: string;
    /** Set once no row is left queued and no created supplier is still being assessed. */
    finishedAt?: string;
    /** Fire-once marker for the message posted into the conversation. */
    chatMessageInjectedAt?: string;
    /**
     * The suppliers created since the last Slack message about the import. Posted, and cleared, once no
     * row is left queued, so that a round of creation is one message however many performs it takes.
     */
    createdSinceNotified?: Array<{ name: string; website: string }>;
  };
}

/**
 * How an IDENTIFY_COMPANY_SITES job ended. `saved` and `kept-existing-draft` leave the company
 * with a draft; the others leave it without one, which the review step shows as an empty table.
 */
export type SiteIdentificationOutcome = 'saved' | 'kept-existing-draft' | 'failed' | 'timed-out' | 'skipped';

/**
 * Resumable state for IDENTIFY_COMPANY_SITES, persisted in job.result so the performer can yield
 * its worker slot between polls of the company-mapping job instead of holding it for minutes.
 */
export interface IdentifyCompanySitesJobResult {
  siteIdentification: {
    /** The company-mapping geolocation job; null until the kick-off succeeded. */
    geolocationJobId: string | null;
    companyName: string;
    companyWebsite: string;
    /** Epoch ms after which a still-running upstream job counts as timed out. */
    deadlineMs: number;
    consecutivePollErrors: number;
    /** Set once the job has settled. */
    outcome?: SiteIdentificationOutcome;
    companyGeolocationId?: number | null;
    siteCount?: number | null;
  };
}

/**
 * PLT-5555 — a single raw holding row for the async large-fund build. Mirrors
 * the identifier fields of the chat `match_all_holdings` input row (name +
 * structured identifiers + weight) but carries NO resolved `companyId`:
 * matching runs headless inside the build job, not in the chat turn. Defined
 * here in `common` (rather than importing the MCP tool's row type) so the jobs
 * layer keeps no dependency on the upserve MCP module.
 */
export interface AsyncFundBuildHolding {
  name?: string;
  /** Customer external id. Carried so a fund built here can be a carry-over source for its next
   * time-version, which prefers EID over every other identifier. */
  eid?: string;
  isin?: string;
  lei?: string;
  vatin?: string;
  ticker?: string;
  cusip?: string;
  website?: string;
  weight?: number;
}

/**
 * PLT-5555 — payload for the async large-fund build job, kicked off by the
 * `create_fund_from_staged` chat tool for funds too large to match +
 * review synchronously in the chat turn (> the sync tier boundary). The job
 * matches every holding headless, auto-accepts the exact-identifier matches,
 * builds the fund, and — when `chatConversationId` is set — posts a "fund built"
 * message back into the originating conversation.
 *
 * `organizationId` is resolved AND scoped to an org the caller belongs to at
 * queue time in the chat turn (via `USE_FUND_UPLOAD`; PLT-5634), so any "which
 * organization?" ambiguity is surfaced to the user synchronously rather than
 * failing later in the worker.
 */
export interface AsyncFundBuildJobPayload {
  type: JobType.ASYNC_FUND_BUILD;
  userId: number;
  organizationId: number;
  fundName: string;
  description?: string;
  isin?: string;
  lei?: string;
  currency?: string;
  asOfDate?: string;
  /**
   * The timespan the user confirmed for the fund's first weight set. Absent, the
   * build falls back to the calendar year of `asOfDate`.
   */
  period?: DateRangeAndYear;
  sourceFilename?: string;
  /**
   * Raw holdings to match + build headless. Legacy path. Mutually exclusive with
   * `stagingId`: the staged-holdings flow (PLT-5520) queues with `stagingId`
   * instead, and the job reads the already-matched, user-reviewed rows from
   * `chat_holdings_staging` rather than re-matching. Optional so both shapes are
   * valid; `payload` is JSONB, so no migration is needed.
   */
  holdings?: AsyncFundBuildHolding[];
  /**
   * PLT-5520: the `chat_holdings_staging` row this build reads from. When set,
   * the job uses the staged, user-reviewed match result (no headless re-match).
   */
  stagingId?: string;
  // Originating `/chat` conversation (= client threadId), set only when kicked
  // off from chat; the completion hook posts the "fund built" message here. The
  // external MCP path leaves it unset and the hook no-ops. `payload` is JSONB,
  // so no migration is needed.
  chatConversationId?: string;
  /**
   * PLT-5514: `messageIndex` of the chat turn that kicked off this build. The
   * completion hook posts the "fund built" message strictly AFTER this index, so
   * on reopen it sorts after its triggering turn even when the (sub-second) build
   * wins the race and injects before that turn's fire-and-forget persist lands.
   * Set only alongside `chatConversationId`; the external MCP path leaves it unset
   * and the hook falls back to a plain tail append.
   */
  chatMessageIndex?: number;
}

/** One user-uploaded document (od mode), stashed in S3 so the async job can read it. */
export interface RevenueSegmentsUploadRef {
  bucket: string;
  key: string;
  filename: string;
}

export interface RevenueSegmentsProductSetGenerationJobPayload {
  type: JobType.REVENUE_SEGMENTS_PRODUCT_SET_GENERATION;
  userId: number;
  companyId: string;
  /** Optional company-name override sent to the pipeline; falls back to the companyId DB name. */
  companyName?: string;
  companyWebsite: string;
  mode: 'upclose' | 'od' | 'url';
  /** Reporting year; required in upclose mode (the year of the resolved annual report). Optional in url mode. */
  year?: number;
  runMapping: boolean;
  runIpr: boolean;
  /** Whether product-mapping uses the mixin embeddings. */
  useMixin: boolean;
  /**
   * PLT-5610: mark the created Blanket product set as an IPRA blanket so it
   * opens with the attribute-editing tools. Driven by the "Run IPR" choice in
   * the APG+ modal ("Run IPR" and "Use mixin" are mutually exclusive there).
   */
  isIpra?: boolean;
  effort?: string;
  mappingEffort?: string;
  /** Free-form provenance label stored on the created product set's `source`. */
  source: string;
  /** upclose mode: the upclose collected-report id whose OCR'd PDF is fed to the pipeline. */
  reportId?: number;
  /** url mode: the web PDF link the job downloads and feeds to the pipeline. */
  pdfUrl?: string;
  /** od mode: the user-uploaded documents to feed to the pipeline. */
  uploads?: RevenueSegmentsUploadRef[];
}

/**
 * PLT-5647: parent of a multi-company APG+ run. The children (one
 * `REVENUE_SEGMENTS_PRODUCT_SET_GENERATION` per company) are queued in the same
 * transaction as this job, so the parent exists only to group them: it gives the
 * batch a single id the status page can be addressed by, and a single row in
 * `/internal/jobs` whose `current`/`total` tracks the whole run.
 */
export interface RevenueSegmentsProductSetBatchJobPayload {
  type: JobType.REVENUE_SEGMENTS_PRODUCT_SET_BATCH;
  userId: number;
  /** Provenance label shared by every product set the batch creates. */
  source: string;
  /** Children queued alongside this job. */
  queuedCount: number;
  /**
   * Input rows rejected at submit time, kept here so the status page can show them
   * after the submit response is gone — a reload or a fresh tab must not lose the
   * reason two of five rows never ran.
   */
  rejected: ApgPlusBatchRejectedRow[];
}

export interface SegmentSplitExtractionJobPayload {
  type: JobType.SEGMENT_SPLIT_EXTRACTION;
  pdfUrl: string;
  companyId: string;
  userId: number;
  batchId: string;
}

export interface SegmentSplitValidationJobPayload {
  type: JobType.SEGMENT_SPLIT_VALIDATION;
}

export interface MixinUpdateJobPayload {
  type: JobType.MIXIN_SCORE_UPDATE;
  version: string;
  batchId: number;
  expressionByCode: Dictionary<string>;
}

export interface CreateMixinJobPayload {
  type: JobType.CREATE_MIXIN;
  productCode: string;
  release: string;
  expression: string;
  title: string;
  description: string | null;
  userId: number;
  template: TemplateTrace | null;
  iprHash?: string;
  ipra?: Partial<IPRAttributes>;
  /**
   * Keys of `ipra` that a person set rather than the APG auto-matching them. Spread straight onto
   * the `product_mixin` row by the create-mixin job, so the name must match the model's column.
   */
  ipraManualFields?: string[];
}

export interface RecomputeMixinJobPayload {
  type: JobType.RECOMPUTE_MIXIN;
  productCode: string;
  version: string;
  expression: string;
}

export interface ComputeMixinExpressionPayload {
  type: JobType.COMPUTE_MIXIN_EXPRESSION;
  expression: string;
}

export interface ExportWeightSetItemsPayload {
  type: JobType.EXPORT_WEIGHT_SET_ITEMS;
  userId: number | undefined;
  sessionAccessId: string | undefined;
  weightSetResultId: number;
  query: GetWeightSetItemResultsQuery;
  columns: QueryWeightSetResultConstituentsColumnId[];
  canReadWeights: boolean;
  release: string;
  totalWeight: number | undefined;
}

export interface ExportGroupfolioCsrdDataPayload {
  type: JobType.EXPORT_GROUPFOLIO_CSRD_DATA;
  userId: number | undefined;
  weightSetResultId: number;
  canReadWeights: boolean;
}

export interface ExportGroupfolioCsrdConstituentDataPayload {
  type: JobType.EXPORT_GROUPFOLIO_CSRD_CONSTITUENT_DATA;
  userId: number | undefined;
  weightSetResultId: number;
  canReadWeights: boolean;
  deliveryMode: 's3' | 'local';
}

export interface ExportAllMetricsPayload {
  type: JobType.EXPORT_EXTERNAL_API_ALL_METRICS;
  userId: number;
  // The release the export targets, resolved at trigger time so the worker
  // uses the exact same release even if "latest" advances in the meantime.
  release: string;
  // Serializable form of the all-metrics query. The worker rehydrates this
  // into a strongly-typed query (casting the string unions, parsing `date`).
  query: AllMetricsExportQuery;
  // Deterministic signature of `query`, used for in-flight dedupe per user.
  querySignature: string;
}

export interface ExportFundScreenerResultsPayload {
  type: JobType.EXPORT_FUND_SCREENER_RESULTS;
  userId: number | undefined;
  /** Identifies the owner when `userId` is undefined, i.e. an anonymous visitor's export. */
  sessionAccessId: string | undefined;
  query: GetFundScreenerResults;
  columns: QueryFundScreenerResultsColumnId[];
  release: string;
}

export interface ImpactStatementFactEvaluationJobPayload {
  type: JobType.IMPACT_STATEMENT_FACT_EVALUATION;
  giseRunId: number;
}

export interface GiseImpactStatementExtractionBatchPayload {
  type: JobType.GISE_IMPACT_TOPIC_EXTRACTION_BATCH;
  giseRunId: number;
}

export interface OpenAIBatchJobPayload {
  type: JobType.LLM_BATCH_JOB;
  batchId: string;
  callback: CallbackPayload;
}

export interface OnDemandMaterialityAssessmentJobPayload {
  type: JobType.ON_DEMAND_MATERIALITY_ASSESSMENT;
  variantId: string;
  draft: boolean;
}

export interface CsrdProductTriggerJobPayload {
  type: JobType.CSRD_PRODUCT_TRIGGERS;
  productDataVersion: string;
  productCodes: string[];
}

export type CsrdBulkPrepareInputSetsJobPayload = {
  type: JobType.CSRD_BULK_PREPARE_INPUT_SETS;
  variantIds: string[];
  userId: number;
};

export type BulkUpdateVariantsJob = {
  type: JobType.BULK_UPDATE_VARIANTS;
  variantIds: string[];
  userId: number;
  variantEdits: VariantBulkEdits;
};

export type RecomputeVariantsTarget = 'pre-release' | 'latest';

export type RecomputePreReleaseForCompaniesJobPayload = {
  type: JobType.RECOMPUTE_PRE_RELEASE_FOR_COMPANIES;
  companyIds: string[];
  scenarioIds?: string[];
  /**
   * Which release the recompute targets. When 'pre-release' (or unset, for
   * backward compatibility with pre-existing queued payloads), the job deletes
   * pre-release variants and re-clones them from latest. When 'latest', the
   * job leaves variants in place and only enqueues compute jobs for them.
   */
  target?: RecomputeVariantsTarget;
  userId?: number;
};

export type CallmControlPayload = {
  type: JobType.CALLM_CONTROL;
  executionMode: 'async' | 'sync';
  delaySeconds?: number; // Optional delay each time before the job is executed
};

export type FinnhubRefreshFundDataPayload = {
  type: JobType.FINNHUB_REFRESH_FUND_DATA;
  userId: number;
  groupId: string;
};

export type FinnhubFetchToStagingPayload = {
  type: JobType.FINNHUB_FETCH_TO_STAGING;
  userId: number;
  groupId: string;
};

export type FinnhubUpsertFromStagingPayload = {
  type: JobType.FINNHUB_UPSERT_FROM_STAGING;
  userId: number;
  groupId: string;
};

export type FinnhubRefreshCompanyFinancialStatementsPayload = {
  type: JobType.FINNHUB_REFRESH_COMPANY_FINANCIAL_STATEMENTS;
  userId: number;
  companyId: string;
};

export type FinnhubRefreshCompaniesFinancialStatementsPayload = {
  type: JobType.FINNHUB_REFRESH_COMPANIES_FINANCIAL_STATEMENTS;
  userId: number;
  companyIds: string[];
};

export type FinnhubSelectiveImportFinancialStatementsPayload = {
  type: JobType.FINNHUB_SELECTIVE_IMPORT_FINANCIAL_STATEMENTS;
  userId: number;
  companyId: string;
  /** Null imports every year Finnhub has, matching the non-selective import. */
  years: number[] | null;
  /** Variants to link the newest created statement to. */
  variantIds: string[];
  /** Archive the statement already held for a period and relink its variants. */
  replaceExisting: boolean;
  /** Skip the company unless Finnhub has a fiscal year at least this recent. */
  requireFiscalYearAtLeast?: number;
  /** Skip banks and insurers, whose balance-sheet lines this import does not map. */
  excludeFinancialInstitutions?: boolean;
};

export enum ArchiveDataOption {
  CSRD_RESOLVED_PRODUCT_TRIGGER = 'csrd-resolved-product-trigger',
  CSRD_PRODUCT_TRIGGER = 'csrd-product-trigger',
  PRODUCT_MIXINS = 'product-mixins',
  IMPACT_SCORES = 'impact-scores',
  PRODUCT_SDG_ALIGNMENT = 'product-sdg-alignment',
  PRODUCT_LABELS = 'product-labels',
}

export const allArchiveDataOptions = Object.values(ArchiveDataOption);

export type ArchiveDataPayload = {
  type: JobType.ARCHIVE_DATA;
  option: ArchiveDataOption;
  version: string;
};

export type StabilizeReleasePayload = {
  type: JobType.STABILIZE_RELEASE;
  releaseNumber: string;
};

export interface PublishIprProductSetJobPayload {
  type: JobType.PUBLISH_IPR_PRODUCT_SET;
  companyId: string;
  blanketProductSetId: number;
  userId: number;
  trigger: GPTProductSetGenerationTrigger; // Required to determine workflow (DD vs DMA)
  /**
   * Whether to create a tailoring variant in addition to the default variant.
   * Defaults to true for backwards compatibility.
   * Set to false for due diligence workflow where only the default variant is needed.
   */
  createTailoringVariant?: boolean;
  /**
   * When true, the job materializes the mixins and publishes the company product
   * set but does NOT upsert a variant or queue variant computation. Recompute is
   * left to the explicit rerun-assessment action. Used by the classic-editor
   * publish of an IPRA blanket, where publishing must not auto-trigger a recompute
   * (see PLT-5571).
   */
  skipVariantComputation?: boolean;
  /** The first computation bypassed review; offer financial statements once results are ready. */
  offerFinancialStatements?: boolean;
  /**
   * PLT-7428: the `/chat` conversation the approval came from. Once the assessment is computed, the
   * job posts there that it is ready, with chips for the briefs to start from. Set by the chat's
   * `publish_dd_product_set`, and by the publish a single chat creation chains without a review step;
   * every other publish (wizard, Edit company dialog, bulk creation) posts nothing.
   */
  chatReadyMessage?: { conversationId: string; offerFinancialStatements?: boolean };
}

export interface HazardMapTilePrefillJobPayload {
  type: JobType.HAZARD_MAP_TILE_PREFILL;
  datasetId: string;
  scenario: string;
  /** Only these stored years; every stored year when absent. */
  years?: number[];
  /**
   * Set on the jobs queued before PLT-7419, one per stored year. Not read: such
   * a job builds every stored year (`prefill-job.ts`).
   */
  year?: number;
  /** Only these zoom levels; every level when absent. */
  levels?: number[];
}

export interface Leena2HarnessJobPayload {
  type: JobType.LEENA2_HARNESS;
  runId: string;
  /** Logged-in user that kicked the run off, or `null` when the run was
   *  triggered via the admin-token route (internal scripts/automation). */
  userId: number | null;
}

export interface Leena2DebugRunJobPayload {
  type: JobType.LEENA2_DEBUG_RUN;
  runId: string;
  /** Logged-in user that kicked the run off, or `null` when the run was
   *  triggered via the admin-token route (internal scripts/automation). */
  userId: number | null;
}

export interface Leena2TeacherComparisonsJobPayload {
  type: JobType.LEENA2_TEACHER_COMPARISONS;
  /** `leena2_comparison_run.id` (source `teacher`). */
  runId: string;
  /** Logged-in user that kicked the run off, or `null` for the admin-token route. */
  userId: number | null;
}

export interface Leena2StudentSamplingJobPayload {
  type: JobType.LEENA2_STUDENT_SAMPLING;
  /** `leena2_comparison_run.id` (source `student`). */
  runId: string;
  /** Logged-in user that kicked the run off, or `null` for the admin-token route. */
  userId: number | null;
}

export interface Leena2SingleAnswersJobPayload {
  type: JobType.LEENA2_SINGLE_ANSWERS;
  /** `leena2_comparison_run.id` (source `singles`). */
  runId: string;
  /** Logged-in user that kicked the run off, or `null` for the admin-token route. */
  userId: number | null;
}

export interface Leena2GlobalSolveJobPayload {
  type: JobType.LEENA2_GLOBAL_SOLVE;
  solveRunId: string;
  /** `solve` runs the spec; `promote` writes a solved, staged run's values to
   *  `impact_metric`. Absent = `solve`. */
  action?: 'solve' | 'promote';
  /** Logged-in user that started the solve, or `null` for the admin-token route. */
  userId: number | null;
}

export interface ArvoRetentionSweepJobPayload {
  type: JobType.ARVO_RETENTION_SWEEP;
}

export interface IngestLlmCostsJobPayload {
  type: JobType.INGEST_LLM_COSTS;
  /**
   * true = a manual "Refresh" run that completes (SUCCESS) so the dashboard's
   * poll resolves. Falsey = the recurring run that re-delays itself daily.
   */
  oneShot?: boolean;
}

export interface DeriveLegacyEntitlementsJobPayload {
  type: JobType.DERIVE_LEGACY_ENTITLEMENTS;
  // Reports what the run would write and writes nothing.
  dryRun?: boolean;
}

export interface RetireExpiredEntitlementContributionsJobPayload {
  type: JobType.RETIRE_EXPIRED_ENTITLEMENT_CONTRIBUTIONS;
}

export interface ReportEntitlementAdoptionJobPayload {
  type: JobType.REPORT_ENTITLEMENT_ADOPTION;
}

export interface PreviewSponsorActivationJobPayload {
  type: JobType.PREVIEW_SPONSOR_ACTIVATION;
  organizationIds: number[];
  // Non-pilot organisations whose members' answers each report also compares.
  comparisonOrganizationIds?: number[];
  // Logs the reports and stores none.
  dryRun?: boolean;
}

/**
 * Status of a job
 */
export enum JobStatus {
  /**
   * Delayed manually
   */
  DELAYED = 'd',
  /**
   * Created but not yet started
   */
  QUEUED = 'q',
  /**
   * Marked running by a worker
   */
  RUNNING = 'r',
  /**
   * ???
   */
  IDLE = 'i',
  /**
   * Errored out
   */
  ERROR = 'e',
  /**
   * Marked success by a worker
   */
  SUCCESS = 's',
}

export const allJobStatuses = Object.keys(JobStatus).map((key: string) => JobStatus[key as keyof typeof JobStatus]);
export const inProgressJobStatuses = new Set([JobStatus.DELAYED, JobStatus.QUEUED, JobStatus.RUNNING, JobStatus.IDLE]);

/**
 * Parameters of a job
 */
export interface BatchRequeueDirtyWeightSetsJobPayload {
  type: JobType.BATCH_REQUEUE_DIRTY_WEIGHT_SETS;
  // Optional filters set by the admin dashboard's "Queue computations" button.
  // Each absent filter means "no constraint on this dimension". With all three
  // absent the job drains the whole table (all active releases).
  release?: string;
  reason?: string;
  sizeBucket?: DirtyWeightSetSizeBucketId;
}

export type JobPayload =
  | BatchRequeueDirtyWeightSetsJobPayload
  | ComputeCompanyJobPayload
  | ComputeCompaniesJobPayload
  | ComputeWeightSetsJobPayload
  | ComputeWeightSetJobPayload
  | MatchWeightSetItemsJobPayload
  | MatchImportSheetJobPayload
  | RematchWeightSetsJobPayload
  | BulkImportConstituentsJobPayload
  | ProductTermMatchCountJobPayload
  | TermPairMatchCountJobPayload
  | ArticleMatchPredictionJobPayload
  | MergedArticleMatchPredictionJobPayload
  | FullScoresJobPayload
  | SecFundImportJobPayload
  | SecEdgarUpdateJobPayload
  | InsideivewImportJobPayload
  | OptimizedScoresJobPayload
  | ScoreOptimizationJobPayload
  | ScoreCrossValidationJobPayload
  | FlowOptimizationJobPayload
  | InlineFlowOptimizationJobPayload
  | FlowPostprocessingJobPayload
  | OptimizedFlowsJobPayload
  | ModelUpdateStepJobPayload
  | ModelUpdateJobPayloadWithVerboseStarter
  | ModelUpdateJobPayload
  | SnapshotJobPayload
  | QuandlExportJobPayload
  | MetricJobPayload
  | SnapshotDowngradeJobPayload
  | SnapshotDeleteJobPayload
  | TriggerAutoUpdateJobPayload
  | CompaniesProblemsJobPayload
  | PopulateCompanyIdentifiersJobPayload
  | AllocationMatrixJobPayload
  | AllocationMatrixJobV2Payload
  | AllocationTableUpdateJobPayload
  | FullScoreContributionPayload
  | TermPairPredictionPayload
  | ConstraintProblemsJobPayload
  | ConstraintProblemsChildJobPayload
  | TriggerAutoConstraintProblemsJobJobPayload
  | TriggerAutoTaxonomyProblemsJobJobPayload
  | UpdateScreenerRowsJobPayload
  | PruneScreenerTableJobPayload
  | RemakeScreenerTableJobPayload
  | MixinUpdateJobPayload
  | CreateMixinJobPayload
  | ComputeMixinExpressionPayload
  | RecomputeMixinJobPayload
  | SearchRefreshElasticSearchEntityIndexJobPayload
  | SearchRefreshSemanticSearchProductIndexJobPayload
  | NotifyAfterApiImportsPayload
  | ComputeVariantResultBatchJobPayload
  | ComputeVariantResultJobPayload
  | AutoGPTWebsiteCrawlJobPayload
  | AutoGPTWebsiteCrawlJobAndReportParsePayload
  | GPTProductSetGenerationJobPayload
  | AsyncFundBuildJobPayload
  | RevenueSegmentsProductSetGenerationJobPayload
  | RevenueSegmentsProductSetBatchJobPayload
  | ExportWeightSetItemsPayload
  | ExportGroupfolioCsrdDataPayload
  | ExportGroupfolioCsrdConstituentDataPayload
  | ExportAllMetricsPayload
  | ExportFundScreenerResultsPayload
  | SegmentSplitExtractionJobPayload
  | ImpactStatementFactEvaluationJobPayload
  | GiseImpactStatementExtractionBatchPayload
  | OnDemandMaterialityAssessmentJobPayload
  | SegmentSplitValidationJobPayload
  | CsrdProductTriggerJobPayload
  | CsrdBulkPrepareInputSetsJobPayload
  | BulkUpdateVariantsJob
  | RecomputePreReleaseForCompaniesJobPayload
  | CallmControlPayload
  | OpenAIBatchJobPayload
  | FinnhubRefreshFundDataPayload
  | FinnhubFetchToStagingPayload
  | FinnhubUpsertFromStagingPayload
  | FinnhubRefreshCompanyFinancialStatementsPayload
  | FinnhubRefreshCompaniesFinancialStatementsPayload
  | FinnhubSelectiveImportFinancialStatementsPayload
  | ArchiveDataPayload
  | StabilizeReleasePayload
  | PublishIprProductSetJobPayload
  | IdentifyCompanySitesJobPayload
  | WriteSupplierRiskWriteUpsJobPayload
  | PrepareSupplierRiskJobPayload
  | SupplierImportWebsiteLookupJobPayload
  | SupplierImportCreateJobPayload
  | ChatFundCoverageReportJobPayload
  | Leena2HarnessJobPayload
  | Leena2DebugRunJobPayload
  | Leena2TeacherComparisonsJobPayload
  | Leena2StudentSamplingJobPayload
  | Leena2SingleAnswersJobPayload
  | Leena2GlobalSolveJobPayload
  | IngestLlmCostsJobPayload
  | ArvoRetentionSweepJobPayload
  | HazardMapTilePrefillJobPayload
  | DeriveLegacyEntitlementsJobPayload
  | PreviewSponsorActivationJobPayload
  | RetireExpiredEntitlementContributionsJobPayload
  | ReportEntitlementAdoptionJobPayload;

export const zExportResult = z.object({
  url: z.string(),
});
export type ExportResult = z.infer<typeof zExportResult>;
export type DependingBatchJobResults = number[] | null;
export type DependingJobCounts = { [key in JobStatus]: number };
export type DataArchivingState = { offset: number };

export type DownloadUrl = string;

export interface GPTProductSetGenerationResult {
  productSetId: number;
  variantId?: string;
  // PLT-5505: ISO timestamp set once the "product set ready" chat message has
  // been injected into the originating `/chat` conversation. Acts as a
  // fire-once marker so a job re-run (retry) does not post a duplicate message.
  chatMessageInjectedAt?: string;
  /**
   * PLT-5647: LLM spend the extraction pipeline reported for this company. Set by
   * the APG+ path (company-mapping returns it per request) so a batch's total cost
   * is readable from the job rows.
   */
  costUsd?: number;
}

/**
 * PLT-5555 — result of the async large-fund build job. `groupId` / `deepLinkUrl`
 * are set once the fund is built; the counts drive the "fund built" chat
 * message. `chatMessageInjectedAt` is the fire-once marker (ISO timestamp) that
 * stops a job re-run from posting a duplicate message — mirrors
 * GPTProductSetGenerationResult. `chatFailureNotifiedAt` (PLT-5598) is the
 * sibling fire-once marker for the best-effort failure message posted when the
 * build job ends in ERROR, so a manual re-run / retry doesn't double-post.
 */
export interface AsyncFundBuildResult {
  groupId?: string;
  deepLinkUrl?: string;
  totalHoldings: number;
  /** Rows linked on an exact structured identifier (ISIN/LEI/VATIN/ticker/CUSIP/EID/UID). */
  autoAcceptedCount: number;
  /**
   * Rows linked on a single-candidate name/website match (a "high-confidence
   * name match" — resolved to exactly one company, but on a fuzzy basis). These
   * are auto-linked headless per the confirmed policy; the ≥0.5%-weight ones are
   * also counted in `flaggedForReviewCount` so the user is nudged to confirm them.
   */
  nameMatchedCount: number;
  flaggedForReviewCount: number;
  matchedWeightPct?: number;
  chatMessageInjectedAt?: string;
  /** PLT-5598 fire-once marker (ISO timestamp) for the failure message posted on ERROR. */
  chatFailureNotifiedAt?: string;
}

/**
 * PLT-7298: a weight set that a chat tool has just created (a new fund, or a new period of one), whose
 * coverage is posted into the conversation once its compute is done. The coverage is read from the
 * computed result as the user sees it, so it is the figure the fund page and the group summary tools
 * give; the share of weight matched at upload, which the tools know up front, is not that figure.
 */
export interface ChatFundCoverageReportJobPayload {
  type: JobType.CHAT_FUND_COVERAGE_REPORT;
  userId: number;
  fundId: string;
  fundName: string;
  weightSetId: number;
  /** "Q3 2026", for a new period of an existing fund. Absent for a new fund. */
  periodLabel?: string;
  chatConversationId: string;
  /** Index of the triggering chat turn, so the message sorts after it (see `AppendAssistantMessageInput`). */
  chatMessageIndex?: number;
}

export interface ChatFundCoverageReportJobResult {
  chatFundCoverageReport: {
    /** ISO time of the first perform; the wait for the compute is bounded from it. */
    startedAt: string;
    /** Fire-once marker: set when the message was posted, or when the job stopped without one. */
    finishedAt?: string;
    chatMessageInjectedAt?: string;
  };
}

/**
 * Intermediate state for PUBLISH_IPR_PRODUCT_SET, persisted in job.result so the
 * performer can yield its worker slot (return false + delay) while child jobs run,
 * instead of busy-waiting and deadlocking small worker fleets (PLT-5026).
 */
export interface PublishIprPhaseState {
  publishPhase: {
    phase: 'awaiting-mixins' | 'awaiting-compute' | 'done';
    /** Product codes whose pre-existing non-master CREATE_MIXIN jobs must drain first */
    watchedMixinProductCodes: string[];
    /** CREATE_MIXIN child jobs queued by the setup phase */
    mixinJobIds: number[];
    /**
     * Epoch ms; mixin wait budget (setup time + 60 min). Extended while a mixin waits for its first
     * worker: see `hasPublishWaitTimedOut`.
     */
    mixinDeadline: number;
    /** Epoch ms; when the first perform ran (stable across requeues, for notifications) */
    performStartedAtMs: number;
    /** Model version resolved at setup; reused by later phases so it can't drift between requeues */
    productTarget: string;
    variantId?: string;
    variantComputationJobId?: number;
    /**
     * Epoch ms; compute wait budget (set when entering awaiting-compute). Extended while the compute
     * waits for its first worker, as `mixinDeadline` is.
     */
    computeDeadline?: number;
    /**
     * Epoch ms; how long to wait for the company's IDENTIFY_COMPANY_SITES job before publishing
     * without it (set on the first wait). PLT-6996.
     */
    siteIdentificationWaitDeadline?: number;
    /** PLT-7428: the chat's "assessment is ready" message is posted, so a re-run does not post it again. */
    chatReadyMessagePosted?: boolean;
  };
}

/**
 * Resumable state for AUTO_DOWNGRADE_SNAPSHOTS. The performer queues one ARCHIVE_DATA
 * child at a time and yields its worker slot while the child runs, instead of
 * busy-waiting on it (which deadlocks small worker fleets — see PLT-5026 for the
 * precedent on PUBLISH_IPR_PRODUCT_SET).
 */
export interface AutoDowngradeState {
  downgradePhase: {
    /** Remaining work in order: per version a 'destroy' followed by one 'archive' per table */
    steps: AutoDowngradeStep[];
    /** ARCHIVE_DATA child currently awaited; set after queueing, cleared once it finishes */
    pendingArchiveJobId?: number;
    /** Epoch ms wall-clock budget for the pending archive job */
    deadline?: number;
  };
}
export type AutoDowngradeStep =
  | { kind: 'destroy'; version: string }
  | { kind: 'archive'; version: string; option: ArchiveDataOption };

/**
 * Resumable state for TRIGGER_AUTO_CONSTRAINT_PROBLEMS_JOB. The performer queues the
 * CONSTRAINT_PROBLEMS spawn job and yields its slot while that job and its
 * CONSTRAINT_PROBLEMS_CHILD_JOB children run, instead of busy-waiting on them.
 */
export interface AutoConstraintProblemsState {
  autoConstraintPhase: {
    phase: 'awaiting-spawn' | 'awaiting-children';
    /** The queued CONSTRAINT_PROBLEMS job; its children are the per-impact-code child jobs */
    constraintJobId: number;
    /** Epoch ms wall-clock budget */
    deadline: number;
  };
}

export type JobResult =
  | ScoreOptimizerResponse
  | ScoreCrossValidationResponse
  | FlowOptimizerResult
  | ModelEntityWithScores[]
  | CreateMixinResult
  | CompanyImportData
  | CompanyImportRowAndMatch[]
  | BusinessSegmentSplitResult
  | ExtractPdfTextResult
  | GhgParseResult
  | ExportResult
  | FullMaterialityResult
  | GroupfolioCsrdExportAggregateData
  | DownloadUrl
  | DependingBatchJobResults
  | DependingJobCounts
  | DataArchivingState
  | GPTProductSetGenerationResult
  | AsyncFundBuildResult
  | PublishIprPhaseState
  | IdentifyCompanySitesJobResult
  | WriteSupplierRiskWriteUpsJobResult
  | SupplierImportWebsiteLookupJobResult
  | SupplierImportCreateJobResult
  | ChatFundCoverageReportJobResult
  | AutoDowngradeState
  | AutoConstraintProblemsState
  | FinnhubSelectiveImportCompanyResult
  | null;

export type JobId = number;
export interface Job {
  id: JobId;
  type: JobType;
  priority: number;
  status: JobStatus;
  payload: JobPayload;
  result: JobResult;
  createdAt: string;
  updatedAt: string;
  timeouts: number;
  startedAt?: string;
  firstStartedAt?: string;
  finishedAt?: string;
  message?: string;
  delayedUntil?: string;
  total?: number;
  current?: number;
  parentId?: number;
  error?: any;
  info?: object;
}

export interface JobStatusResponse {
  status: JobStatus | undefined;
  message?: string;
  info?: object;
}

export interface JobCount {
  type: JobType;
  status: JobStatus;
  count: number;
}

export interface JobResultSuccess<T extends JobResult = JobResult> {
  status: JobStatus.SUCCESS;
  result: T;
}

export interface JobResultError {
  status: JobStatus.ERROR;
  error: string;
}

export interface JobResultPending {
  status: JobStatus.DELAYED | JobStatus.IDLE | JobStatus.QUEUED | JobStatus.RUNNING;
}

export type JobResultResponse<T extends JobResult = JobResult> =
  | JobResultSuccess<T>
  | JobResultError
  | JobResultPending;

export type JobResultResponseWithMessage<T extends JobResult = JobResult> = JobResultResponse<T> & {
  message: string | null;
};
